import "@mantine/core/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/dates/styles.css";
import "./styles.css";

import { MantineProvider } from "@mantine/core";
import {
	QueryClient,
	QueryClientProvider,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import { getUserOptions } from "@/api/auth";
import { ConfirmationDialogProvider } from "./context/ConfirmationDialog";
import { WalletProvider } from "./providers/walletProvider";
import { routeTree } from "./routeTree.gen";
import { mantineTheme } from "./shared/theme";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 500,
		},
	},
});

const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	defaultPendingMs: 0,
	defaultStructuralSharing: true,
	defaultViewTransition: true,
	scrollRestoration: true,
	context: {
		queryClient,
		isAuthenticated: false,
		user: null,
	},
});

function App() {
	const auth = useSuspenseQuery(getUserOptions());
	return (
		<RouterProvider
			router={router}
			context={{
				queryClient,
				isAuthenticated: !!auth?.data,
				user: auth?.data,
			}}
		/>
	);
}

export function Root() {
	return (
		<QueryClientProvider client={queryClient}>
			<MantineProvider theme={mantineTheme}>
				<WalletProvider>
					<ConfirmationDialogProvider>
						<App />
					</ConfirmationDialogProvider>
				</WalletProvider>
			</MantineProvider>
		</QueryClientProvider>
	);
}

// biome-ignore lint/style/noNonNullAssertion: will surely exist in index.html
const rootElement = document.getElementById("app")!;

if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(<Root />);
}

// Register things for typesafety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
